import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiPlus, FiFolder, FiUpload, FiDownload, FiBarChart2 } from "react-icons/fi";
import { useToast } from "../components/ToastProvider";
import { api } from "../utils/api";
import StatCards from "../components/StatCards";
import LinkList from "../components/LinkList";
import CreateLinkModal from "../components/CreateLinkModal";
import BulkLinkImportModal from "../components/BulkLinkImportModal";
import "../styles/Dashboard.css";
import "../styles/Campaigns.css";

export default function CampaignDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [links, setLinks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [linksLoading, setLinksLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const initialLoadDone = useRef(false);

  const fetchCampaignData = useCallback(async () => {
    try {
      setLoading(true);

      const [campaignData, analyticsData] = await Promise.all([
        api.getCampaign(id),
        api.getCampaignAnalytics(id),
      ]);

      setCampaign(campaignData);
      setAnalytics(analyticsData || null);
    } catch (err) {
      console.error(err);
      addToast("Failed to load campaign details", "error");
      navigate("/campaigns");
    } finally {
      setLoading(false);
    }
  }, [id, addToast, navigate]);

  const fetchLinks = useCallback(async () => {
    try {
      setLinksLoading(true);
      const data = await api.getLinks(1, 100, "", "", id);
      setLinks(data.links || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLinksLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (campaign && campaign.name) {
      document.title = `${campaign.name} - Campaigns - LinkSquire`;
    }
  }, [campaign]);

  useEffect(() => {
    if (!initialLoadDone.current) {
      fetchCampaignData();
      fetchLinks();
      initialLoadDone.current = true;
    }
  }, [fetchCampaignData, fetchLinks]);

  const handleLinkCreated = () => {
    setShowCreate(false);
    fetchLinks();
    fetchCampaignData();
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await api.exportLinks({ campaign_id: id });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `linksquire-campaign-${id}-links.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addToast("Campaign links exported as CSV", "success");
    } catch (err) {
      addToast(err.message || "Failed to export campaign links", "error");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard page-fade-in">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading campaign details...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="dashboard page-fade-in">
        <div className="campaign-empty-state">
          <div className="campaign-empty-icon">
            <FiFolder size={32} />
          </div>
          <h3>Campaign not found</h3>
          <p>
            The campaign you are looking for might have been deleted or never existed.
          </p>
          <Link to="/campaigns" className="btn-primary" style={{ textDecoration: "none", display: "inline-flex" }}>
            Back to Campaigns
          </Link>
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const detailed = analytics?.analytics || {};

  const totalClicks = summary.periodClicks || 0;
  const recentClicks = summary.recentClicks || 0;
  const activeLinks = summary.activeLinks || 0;

  const byDevice = Array.isArray(detailed.by_device) ? detailed.by_device : [];
  const byCountry = Array.isArray(detailed.by_country) ? detailed.by_country : [];
  const byReferrer = Array.isArray(detailed.by_referrer) ? detailed.by_referrer : [];
  const byBrowser = Array.isArray(detailed.by_browser) ? detailed.by_browser : [];

  const totalDeviceClicks = byDevice.reduce((sum, item) => sum + (item.count || 0), 0);
  const mobileClicks = byDevice
    .filter((item) => String(item.name || "").toLowerCase() === "mobile")
    .reduce((sum, item) => sum + (item.count || 0), 0);
  const mobileTrafficPct = totalDeviceClicks > 0 ? Math.round((mobileClicks / totalDeviceClicks) * 100) : 0;

  const topSource = byReferrer[0]?.name || "Direct";
  const topRegion = byCountry[0]?.name || "Unknown";
  const topDevice = byDevice[0]?.name || "Unknown";
  const topBrowser = byBrowser[0]?.name || "Unknown";

  const previousWindowClicks = Math.max(totalClicks - recentClicks, 0);
  const averagePriorDayClicks = previousWindowClicks / 29;
  const clickTrendPct = averagePriorDayClicks > 0
    ? ((recentClicks - averagePriorDayClicks) / averagePriorDayClicks) * 100
    : 0;

  const mobileVsNonMobilePct = totalDeviceClicks > 0
    ? ((mobileClicks - (totalDeviceClicks - mobileClicks)) / totalDeviceClicks) * 100
    : 0;

  return (
    <div className="dashboard page-fade-in">
      <Link to="/campaigns" className="campaign-back-link">
        <FiArrowLeft /> Back to Campaigns
      </Link>

      <div className="campaign-detail-header">
        <div className="campaign-detail-info">
          <div className="campaign-detail-title-row">
            <div
              className="campaign-detail-dot"
              style={{ backgroundColor: campaign.color || "#2383e2" }}
            />
            <h1 className="campaign-detail-title">{campaign.name}</h1>
          </div>
          {campaign.description && (
            <p className="campaign-detail-desc">{campaign.description}</p>
          )}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setShowImport(true)} className="btn-secondary">
            <FiUpload size={16} /> Import CSV
          </button>
          <button onClick={handleExport} className="btn-secondary" disabled={exporting}>
            <FiDownload size={16} /> {exporting ? "Exporting..." : "Export CSV"}
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <FiPlus size={18} /> New Link
          </button>
        </div>
      </div>

      {analytics && (
        <div className="campaign-stats-row">
          <StatCards
            summary={{
              total_clicks: totalClicks,
              unique_visitors: recentClicks,
              total_links: activeLinks,
            }}
          />
        </div>
      )}

      {analytics && (
        <aside className="campaign-snapshot-panel" aria-label="Campaign snapshot">
          <div className="campaign-snapshot-top">
            <p>Campaign Snapshot</p>
            <span>Live</span>
          </div>
          <div className="campaign-snapshot-metric-row">
            <MetricCard
              value={formatCompact(totalClicks)}
              label="Clicks this month"
              trend={formatSignedPercent(clickTrendPct)}
            />
            <MetricCard
              value={`${mobileTrafficPct}%`}
              label="Mobile traffic"
              trend={formatSignedPercent(mobileVsNonMobilePct)}
            />
          </div>
          <div className="campaign-snapshot-list">
            <AnalyticsItem label="Top source" value={topSource} />
            <AnalyticsItem label="Top region" value={topRegion} />
            <AnalyticsItem label="Best device" value={`${topDevice} / ${topBrowser}`} />
          </div>
          <div className="campaign-snapshot-footer">
            <FiBarChart2 size={15} /> Trends are based on the last 30 days of data and compare recent performance to the prior period.
          </div>
        </aside>
      )}

      <div className="links-section">
        <div className="links-header">
          <h2>Campaign Links</h2>
        </div>

        {linksLoading && links.length === 0 ? (
          <div className="loading-state">
            <div className="spinner" />
          </div>
        ) : links.length === 0 ? (
          <div className="campaign-links-empty">
            <h3>No links added yet</h3>
            <p>
              Start adding short links to this campaign to see aggregated analytics.
            </p>
            <button onClick={() => setShowCreate(true)} className="btn-primary">
              <FiPlus /> Add First Link
            </button>
          </div>
        ) : (
          <LinkList
            links={links}
            setLinks={setLinks}
            setTotal={() => {}}
            refreshSummary={fetchCampaignData}
            refreshLinks={fetchLinks}
          />
        )}
      </div>

      {showCreate && (
        <CreateLinkModal
          onClose={() => setShowCreate(false)}
          onCreated={handleLinkCreated}
          prefillCampaignId={id}
        />
      )}

      {showImport && (
        <BulkLinkImportModal
          onClose={() => setShowImport(false)}
          defaultCampaignId={id}
          onImported={() => {
            fetchLinks();
            fetchCampaignData();
          }}
        />
      )}
    </div>
  );
}

function MetricCard({ value, label, trend }) {
  return (
    <div className="campaign-snapshot-metric-card">
      <h4>{value}</h4>
      <p>{label}</p>
      <span>{trend}</span>
    </div>
  );
}

function AnalyticsItem({ label, value }) {
  return (
    <div className="campaign-snapshot-item">
      <p>{label}</p>
      <span>{value}</span>
    </div>
  );
}

function formatCompact(value) {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value || 0);
}

function formatSignedPercent(value) {
  const safe = Number.isFinite(value) ? value : 0;
  const sign = safe >= 0 ? "+" : "";
  return `${sign}${safe.toFixed(1)}%`;
}
